import type { Server } from "./types";

/**
 * How full a server is, for display.
 *
 * This mirrors the rule the API places on — the tightest single resource, not
 * an average, because a box with 4% disk free is full whatever its CPU is
 * doing. It is deliberately a copy rather than a value the API sends: this one
 * exists to draw a bar, and the decision it echoes is made server-side against
 * numbers the console never sees mid-request.
 *
 * Null means nothing has been sampled. It is never rendered as 0% — a server
 * reporting nothing must not read as an idle one.
 */
export function tightestUsage(capacity: Server["capacity"]): number | null {
  if (!capacity.collectedAt) return null;

  const used: number[] = [];
  if (capacity.cpuPercent !== null) used.push(capacity.cpuPercent / 100);

  const memory = fraction(capacity.memoryUsedBytes, capacity.memoryTotalBytes);
  if (memory !== null) used.push(memory);

  const disk = fraction(capacity.diskUsedBytes, capacity.diskTotalBytes);
  if (disk !== null) used.push(disk);

  return used.length > 0 ? Math.max(...used) : null;
}

/** The threshold at which the API stops placing new tenants on a server. */
export const SATURATED_ABOVE = 0.9;

function fraction(used: number | null, total: number | null): number | null {
  if (used === null || total === null || total <= 0) return null;
  return Math.min(1, Math.max(0, used / total));
}
